import { useCallback, useSyncExternalStore } from 'react';

/**
 * Entwürfe (Beschreibung, Kommentar) je Karte. Sie überstehen Reiterwechsel, Schließen, erneutes
 * Öffnen und Neuladen (sessionStorage dieses Tabs), gelten aber immer nur für genau eine Karte
 * (Schlüssel `<kartenId>:<art>`). Solange es Entwürfe gibt, warnt der Browser vor dem Schließen.
 */
const STORE = 'kaba.drafts';
const drafts = new Map<string, string>(load());

function load(): [string, string][] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(STORE) ?? '{}') as Record<string, unknown>;
    return Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === 'string');
  } catch {
    return [];
  }
}

function persist() {
  try {
    if (drafts.size) sessionStorage.setItem(STORE, JSON.stringify(Object.fromEntries(drafts)));
    else sessionStorage.removeItem(STORE);
  } catch {
    /* Speicher voll oder gesperrt: Entwurf bleibt wenigstens bis zum Neuladen */
  }
}
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export type DraftKind = 'description' | 'comment';
export const draftKey = (cardId: string, kind: DraftKind) => `${cardId}:${kind}`;

export function getDraft(key: string): string | undefined {
  return drafts.get(key);
}

export function setDraft(key: string, value: string | undefined) {
  if (value === undefined) {
    if (!drafts.has(key)) return;
    drafts.delete(key);
  } else {
    if (drafts.get(key) === value) return;
    drafts.set(key, value);
  }
  persist();
  emit();
}

export function useDraft(key: string): [string | undefined, (v: string | undefined) => void] {
  const value = useSyncExternalStore(subscribe, () => drafts.get(key));
  const set = useCallback((v: string | undefined) => setDraft(key, v), [key]);
  return [value, set];
}

/** Hat diese Karte ungespeicherte Entwürfe? */
export function cardHasDrafts(cardId: string): boolean {
  for (const [k, v] of drafts) if (k.startsWith(`${cardId}:`) && v.trim()) return true;
  return false;
}

/** Gibt es irgendwo einen nicht gespeicherten Entwurf? */
export function hasAnyDraft(): boolean {
  for (const v of drafts.values()) if (v.trim()) return true;
  return false;
}

export function clearAllDrafts() {
  drafts.clear();
  persist();
  emit();
}

window.addEventListener('beforeunload', (e) => {
  for (const v of drafts.values())
    if (v.trim()) {
      e.preventDefault();
      return;
    }
});

/** Hat diese Karte ungespeicherte Änderungen? (Kommentarentwurf mit Text oder geänderte Beschreibung) */
function cardUnsaved(cardId: string): boolean {
  if (drafts.get(`${cardId}:comment`)?.trim()) return true;
  const d = drafts.get(`${cardId}:description`);
  if (d === undefined) return false;
  try {
    const base = JSON.parse(drafts.get(`${cardId}:descriptionBase`) ?? 'null') as { html: string } | null;
    return !base || base.html !== d;
  } catch {
    return true;
  }
}

export function useCardUnsaved(cardId: string): boolean {
  return useSyncExternalStore(subscribe, () => cardUnsaved(cardId));
}
